'use client';

import { createPortal } from 'react-dom';
import { UserInterfaceElements, MimeType, supportsVideo, supportsBrowser, supportsVideoExport } from '@cesdk/cesdk-js';
import { reportErrorToFaro } from '@sendible/common';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useToastNotificationContext } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { CeSDKWrapper } from './index.styles';

import {
  PAGE_FORMATS_INSERT_ENTRY_DOCK,
  PAGE_FORMATS_INSERT_ENTRY_ASSET,
  formatAssetsToPresets,
  pageFormatI18n,
  PageFormatAsset,
} from './VideoEditor/PageFormatAssetLibrary';
import PAGE_FORMAT_ASSETS from './VideoEditor/PageFormatAssets.json';
import { caseAssetPath } from './VideoEditor/util';
import CreativeEditor, { useConfig, useConfigure } from './lib/CreativeEditor';
import loadAssetSourceFromContentJSON from './lib/loadAssetSourceFromContentJSON';
import { createApplyFormatAsset } from './VideoEditor/createApplyFormatAsset';
import { useUploadFiles } from '../../models/uploads';
import { EDITOR_CLOSE_VIDEO, EDITOR_CLOSE_WITH_ERROR, EDITOR_VIDEO_EXPORT } from '.';

type EditorProps = {
  originalMediaId: number;
  originalUrl: string;
  sourceSets: { url: string; height: string; width: string }[];
  resetState: () => void;
  fileName: string;
  serviceSet: ServiceSetTypeVideo;
};

const Editor = ({ originalMediaId, originalUrl, sourceSets, resetState, fileName, serviceSet }: EditorProps) => {
  const { showToastNotification } = useToastNotificationContext();
  const { t } = useTranslation('media_library');
  const uploadFiles = useUploadFiles({ uploadingFrom: 'editor' });

  const closeAndTriggerError = (message: string) => {
    window.pendo.track('[CE.SDK] Editor Error', { errorMessage: message });
    window.parent.postMessage({ event: EDITOR_CLOSE_WITH_ERROR, message, status: 'success' }, '*');
    resetState();
  };

  const matchedServiceAssets = PAGE_FORMAT_ASSETS.assets.filter((asset) => !!asset.meta.service.includes(serviceSet));
  const filteredCropAssets = matchedServiceAssets.length === 0 ? PAGE_FORMAT_ASSETS.assets : matchedServiceAssets;
  const filtedPageFormatAssets = { ...PAGE_FORMAT_ASSETS, assets: filteredCropAssets };

  const config = useConfig(
    () => ({
      role: 'Adopter',
      theme: 'light',
      license: 'VHjEPgPLBuDp-hnJmlQe-UAEu1GdyVUXuNAyIuvheFkzoxpkPdl4A1oKbCGgmjsl',
      i18n: {
        en: {
          ...pageFormatI18n(filtedPageFormatAssets.assets as PageFormatAsset[]),
        },
      },
      callbacks: {
        onUpload: 'local',
      },
      ui: {
        pageFormats: formatAssetsToPresets(filtedPageFormatAssets),
        elements: {
          view: 'default',
          blocks: {
            '//ly.img.ubq/page': {
              format: false,
              manage: false,
            },
          },
          panels: {
            settings: false,
            inspector: {
              floating: true,
            },
          },
          navigation: {
            position: UserInterfaceElements.NavigationPosition.Top,
            action: {
              export: true,
            },
          },
          libraries: {
            insert: {
              floating: true,
              autoClose: false,
            },
            replace: {
              floating: true,
            },
          },
        },
      },
    }),
    []
  );

  const configure = useConfigure(async (instance) => {
    const stickerAssetEntry = instance.ui.getAssetLibraryEntry('ly.img.sticker');
    const supportsExport = await supportsVideoExport();
    const supportBrowser = supportsBrowser();
    const supportVideo = supportsVideo();

    if (!supportsExport || !supportBrowser || !supportVideo) {
      closeAndTriggerError(t('video_not_supported'));
    }

    const newSourceSet = sourceSets.map((source) => {
      return {
        uri: source.url,
        height: source.height,
        width: source.width,
      };
    });
    const defaulNavOrder = instance.ui.getNavigationBarOrder();
    const newNavOrder = defaulNavOrder.filter((item) => item.id !== 'ly.img.actions.navigationBar');

    await instance.addDefaultAssetSources();

    await instance.engine.scene.createFromVideo(originalUrl).catch(() => {
      closeAndTriggerError(t('video_format_not_supported'));
    });

    newSourceSet.forEach(async (sourceSet) => {
      await instance.engine.block.addVideoFileURIToSourceSet(
        instance.engine.block.findByType('fill/video')[0],
        'fill/video/sourceSet',
        sourceSet.uri
      );
    });

    instance.engine.editor.setSettingBool('page/title/show', false);

    instance.ui.addAssetLibraryEntry({
      id: 'ly.img.video.scene',
      sourceIds: ['ly.img.video.scene'],
    });

    instance.ui.addAssetLibraryEntry(PAGE_FORMATS_INSERT_ENTRY_ASSET);

    // the follow TS was disabled, I couldn't find a solution and this is how the imgly demos work
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    loadAssetSourceFromContentJSON(instance.engine, filtedPageFormatAssets, caseAssetPath('/page-formats'), createApplyFormatAsset(instance.engine));

    if (stickerAssetEntry && stickerAssetEntry.sourceIds) {
      instance.ui.updateAssetLibraryEntry('ly.img.sticker', {
        sourceIds: [...stickerAssetEntry.sourceIds, 'ly.img.video.giphy.sticker'],
        gridColumns: 2,
      });
    }

    instance.ui.registerComponent('export.button', ({ builder: { Button }, engine }) => {
      Button('export', {
        label: 'Save Video',
        color: 'accent',
        onClick: async () => {
          const abortSignal = new AbortController();
          const dialog = instance.ui.showDialog({
            type: 'loading',
            actions: {
              label: 'Cancel',
              onClick: (context) => {
                instance.ui.closeDialog(context.id);
                abortSignal.abort();
              },
            },
            clickOutsideToClose: false,
            content: {
              title: 'Saving video...',
              message: 'Do not close this window.',
            },
            progress: 0,
          });

          const page = instance.engine.scene.getCurrentPage();

          try {
            const blob = await engine.block.exportVideo(
              page as number,
              MimeType.Mp4,
              (numberOfRenderedFrames, numberOfEncodedFrames, totalNumberOfFrames) => {
                const progress = Math.round((numberOfRenderedFrames / totalNumberOfFrames) * 100);

                instance.ui.updateDialog(dialog, { progress });
              },
              {
                abortSignal: abortSignal.signal,
              }
            );

            if (blob) {
              const file = new File([blob], fileName, {
                type: 'video/mp4',
                lastModified: Date.now(),
              });

              uploadFiles([file], (mediaId: string) => {
                window.pendo.track('[CE.SDK] Editor Export', { fileType: 'video' });
                window.parent.postMessage({ event: EDITOR_VIDEO_EXPORT, mediaId, originalMediaId }, '*');
                window.parent.postMessage({ event: EDITOR_CLOSE_VIDEO }, '*');
                resetState();
              });
            } else {
              throw new Error('Video Blob not generated');
            }
          } catch (error: unknown) {
            if (error instanceof Error && error?.message === 'AbortSignal received') {
              return;
            }

            reportErrorToFaro(error, 'CreativeEditor config');

            instance.ui.closeDialog(dialog);
            showToastNotification({
              notificationText: t('video_not_saved') as string,
              type: NotificationTypeEnum.Error,
              showCloseButton: true,
            });
          }
        },
      });
    });

    instance.ui.registerComponent('close.button', ({ builder: { Button }, engine }) => {
      Button('close', {
        label: 'Close',
        variant: 'regular',
        icon: '@imgly/Cross',
        onClick: async () => {
          window.pendo.track('[CE.SDK] Editor Close', { fileType: 'video' });
          window.parent.postMessage({ event: EDITOR_CLOSE_VIDEO }, '*');
          resetState();
        },
      });
    });

    instance.ui.setNavigationBarOrder([...newNavOrder, 'close.button', 'export.button']);

    instance.ui.setDockOrder([
      PAGE_FORMATS_INSERT_ENTRY_DOCK,
      {
        id: 'ly.img.assetLibrary.dock',
        key: 'ly.img.upload',
        icon: '@imgly/Upload',
        label: 'libraries.ly.img.upload.label',
        entries: ['ly.img.upload'],
      },
      {
        id: 'ly.img.assetLibrary.dock',
        key: 'ly.img.text',
        icon: '@imgly/Text',
        label: 'libraries.ly.img.text.label',
        entries: ['ly.img.text'],
      },
      {
        id: 'ly.img.assetLibrary.dock',
        key: 'ly.img.vectorpath',
        icon: '@imgly/Shapes',
        label: 'libraries.ly.img.vectorpath.label',
        entries: ['ly.img.vectorpath'],
      },
      {
        id: 'ly.img.assetLibrary.dock',
        key: 'ly.img.sticker',
        icon: '@imgly/Sticker',
        label: 'libraries.ly.img.sticker.label',
        entries: ['ly.img.sticker'],
      },
    ]);

    await instance.addDemoAssetSources({
      sceneMode: 'Video',
      excludeAssetSourceIds: ['ly.img.audio', 'ly.img.video.template'],
    });

    return () => null;
  }, []);

  return (
    <div className="cesdkWrapperStyle">
      {createPortal(
        <CeSDKWrapper>
          <CreativeEditor
            className="cesdk-photo-editor cesdkStyle"
            config={config}
            style={{}}
            configure={configure}
          />
        </CeSDKWrapper>,
        document.getElementById('modal-root') as HTMLElement
      )}
    </div>
  );
};

export default Editor;
